import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { PhonicsSound, useGetPhonicsSoundsQuery } from '@/api/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { Lines, SymbolType, CdnObjectType } from '@/types';
import { findSoundsInString } from '@/utils/sounds';
import { CanvasTrace, AudioPlayer } from '@/components';
import { getSoundExtraData } from '@/utils/soundsData';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { WhitePanel } from '../components/WhitePanel';
import { Activity, ActivityStep } from '../Activity';
import { Toolbar } from '../components/Toolbar';
import { ActivityTypeProps } from '../types';

interface TraceNewSoundProps {
  sound: string;
}

interface TraceNewSound {
  asBitmap: () => string | undefined;
  asVector: () => Lines | undefined;
}

const TraceNewSound = forwardRef<TraceNewSound, TraceNewSoundProps>(
  ({ sound }, ref) => {
    const playerRef = useRef<AudioPlayer>(null);
    const traceRef = useRef<CanvasTrace>(null);

    const { sendAlert } = useUi();

    useImperativeHandle(ref, () => ({
      asBitmap: () => traceRef.current?.asBitmap(),
      asVector: () => traceRef.current?.asVector(),
    }));

    const mediaUrl = useAssetCdn(sound, SymbolType.Sound, CdnObjectType.Audio);

    const [playedOnce, setPlayedOnce] = useState(false);

    const [playerReady, setPlayerReady] = useState(false);

    const [soundObject, setSoundObject] = useState<PhonicsSound | undefined>(
      undefined,
    );

    const { data: soundsData } = useGetPhonicsSoundsQuery();

    const {
      arrows: arrowsPath,
      lines: linesPath,
      lineYOffset,
    } = useMemo(() => {
      if (!soundObject) {
        return {
          arrows: undefined,
          lines: undefined,
          lineYOffset: 0,
        };
      }

      return getSoundExtraData(soundObject.name as any);
    }, [soundObject]);

    useEffect(() => {
      if (!soundsData) {
        return;
      }

      setSoundObject(soundsData.getPhonicsSounds.find((s) => s.name === sound));
    }, [soundsData, sound]);

    return (
      <>
        <Toolbar
          onPlay={() => {
            playerRef.current?.play();
            traceRef.current?.animateGuide();
            setPlayedOnce(true);
          }}
          hintPlay={!playedOnce}
          playDisabled={!playerReady}
          onRefresh={() => traceRef.current?.reset()}
        />

        <WhitePanel mode="basic" kind="fill" color="paper">
          {soundObject ? (
            <div className="relative w-80 mx-auto">
              <CanvasTrace
                ref={traceRef}
                className="w-80 h-80 mx-auto"
                drawWidth={320}
                drawHeight={320}
                traceBasis={soundObject.referencePath || undefined}
                showBasis={false}
                traceGuide={soundObject.guidePath || undefined}
                additionalPrefixPaths={[
                  ...(linesPath && linesPath !== ''
                    ? [
                        {
                          path: linesPath,
                          color: '#bebebe',
                          strokeWidth: 0,
                          yOffset: lineYOffset || 0,
                        },
                      ]
                    : []),
                ]}
                additionalSuffixPaths={[
                  ...(arrowsPath && arrowsPath !== ''
                    ? [
                        {
                          path: arrowsPath,
                          strokeWidth: 0,
                        },
                      ]
                    : []),
                ]}
                seed="topRight"
                showScore={false}
                includeBgOnLines={false}
              />
            </div>
          ) : (
            <p>Unable to build trace object</p>
          )}
        </WhitePanel>

        <AudioPlayer
          ref={playerRef}
          src={mediaUrl || ''}
          onReadySrc={() => setPlayerReady(true)}
          onLoadTimeoutSrc={() =>
            sendAlert('We could not load some of the audio for this lesson.', {
              type: 'warning',
              title: 'There was a problem',
            })
          }
        />
      </>
    );
  },
);

TraceNewSound.displayName = 'TraceNewSound';

interface SpellingNewSoundsStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  sound: string;
}

const SpellingNewSoundsStep = ({
  activity,
  mode,
  wrapperClassName,
  index,
  total,
  sound,
  isPreview,
}: SpellingNewSoundsStepProps) => {
  const traceReviewSoundRef = useRef<TraceNewSound | null>(null);

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      usePanel
      title={`Hear the sound. Watch and write the letter.`}
      showTitle={mode === 'TEACH'}
      showActivityPagination={false}
      isPreview={isPreview}
      getActivityResponseMetaData={() => ({
        text: sound,
        base64Image: traceReviewSoundRef.current?.asBitmap(),
        points: JSON.stringify(traceReviewSoundRef.current?.asVector()),
      })}
    >
      <TraceNewSound ref={traceReviewSoundRef} sound={sound} />
    </ActivityStep>
  );
};

export const SpellingNewSounds = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const sounds = findSoundsInString(
    soundsData?.getPhonicsSounds.map((sound) => sound.name) || [],
    activity.sounds,
  );

  return (
    <Activity
      steps={[
        ...(mode === 'TEACH'
          ? [
              <ActivityTitleStep
                key={`${activity.id}-title`}
                activity={activity}
                mode={mode}
                wrapperClassName={wrapperClassName}
                isPreview={isPreview}
                index={0}
                total={sounds.length + 1}
              />,
            ]
          : []),
        ...sounds?.flatMap((sound, index) => [
          <SpellingNewSoundsStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={sounds.length + 1}
            sound={sound}
          />,
        ]),
      ]}
    />
  );
};
